import { useEffect, useRef } from 'react'

import { ActionInfo, Tabs, panelId, tabId, type TabItem } from '@krp/ui-kit'

import { linkState } from '../model/link'
import { HARDWARE_TABS, isHardwareTab, tabState, type NextStepVm } from '../model/tabs'
import type { EquipmentSnapshot, HardwareActions, HardwareLinks, HardwareStatus, HardwareTabId } from '../model/types'
import './hardware.css'
import { DriversTab } from './DriversTab'
import { KassasTab } from './KassasTab'
import { LinkStrip } from './LinkStrip'
import { ScreenReaderText } from './Mark'
import { TerminalsTab } from './TerminalsTab'
import { Verdict } from './Verdict'

export const HARDWARE_TABS_PREFIX = 'hardware'

export interface HardwareScreenProps {
  snapshot: EquipmentSnapshot
  links: HardwareLinks
  status: HardwareStatus
  /** Открытый таб из адреса; `null` — адрес без таба, окно ещё выбирает (п. 2.3). */
  tab: HardwareTabId | null
  actions: HardwareActions
  onSelectTab: (tab: HardwareTabId) => void
}

/**
 * «Оборудование», версия 2 (EQW2): полоса связи с агентом, вердикт «можно ли продавать» и три таба «Драйверы / Кассы / Терминалы».
 * Таб — адрес (`/hardware/:tab`), его меняет только человек. Каждое состояние — значок и слово, а не только цвет (CLAUDE.md §7).
 * Экран только рисует снимок и вызывает действия: ни запросов, ни хранилища.
 */
export function HardwareScreen({ snapshot, links, status, tab, actions, onSelectTab }: HardwareScreenProps) {
  const link = linkState(snapshot.link, snapshot.now)
  const available = link.phase === 'connected' || link.phase === 'outdated' || link.phase === 'lost'
  const stripRef = useRef<HTMLDivElement>(null)
  // Узкий экран: полоса прокручивается вбок, и открытый таб (адрес из закладки, F5) не должен остаться за краем.
  useEffect(() => {
    const selected = stripRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    if (selected != null && typeof selected.scrollIntoView === 'function') selected.scrollIntoView({ block: 'nearest', inline: 'nearest' })
  }, [tab])

  const items: TabItem[] = HARDWARE_TABS.map((id) => {
    const state = tabState(id, snapshot)
    return {
      id,
      label: state.title,
      icon: (
        <>
          <span aria-hidden="true">{state.glyph}</span> <ScreenReaderText>{state.word}: </ScreenReaderText>
        </>
      ),
      ...(state.badge !== null ? { badge: state.badge } : {}),
      hint: state.label,
    }
  })

  const next = (step: NextStepVm) => {
    switch (step.id) {
      case 'copy-origin':
        actions.copyOrigin()
        break
      case 'update-agent':
      case 'download-agent':
        if (links.agentInstaller !== null) actions.downloadAgent()
        break
      case 'recheck':
        actions.recheck()
        break
      case 'download-driver':
        if (step.driverId !== null && (links.drivers[step.driverId] ?? null) !== null) actions.downloadDriver(step.driverId)
        break
      case 'check-drivers':
        actions.checkDrivers()
        break
      case 'pick-agent':
      case 'scan-kassa':
      case 'scan-terminal':
        break
    }
    // Переход к причине — предложением: человек видит, куда его ведут, и сам решает дальше.
    onSelectTab(step.tab)
  }

  const common = { locked: link.actionsDisabled, busy: status.busy, available, links, actions, onOpenTab: onSelectTab }

  return (
    <section aria-label="Оборудование" className="hw" data-stale={link.stale || undefined}>
      {status.notice !== null && (
        <p role={status.notice.tone === 'error' ? 'alert' : 'status'} className={`hw-notice hw-notice--${status.notice.tone}`}>
          {status.notice.tone === 'error' ? '✕ ' : '✓ '}
          {status.notice.text}
        </p>
      )}

      <LinkStrip snapshot={snapshot} links={links} status={status} actions={actions} />
      <Verdict snapshot={snapshot} onNext={next} />

      <div className="hw-tabs" ref={stripRef}>
        <ActionInfo id="platform.hardware.tabs" label="Вкладки оборудования" layout="block">
          <Tabs
            label="Разделы оборудования"
            items={items}
            selected={tab ?? ''}
            onSelect={(id) => {
              if (isHardwareTab(id) && id !== tab) onSelectTab(id)
            }}
            idPrefix={HARDWARE_TABS_PREFIX}
          />
        </ActionInfo>
      </div>

      {tab === null ? (
        <p role="status" className="hw-hint">
          Определяем, с чего начать…
        </p>
      ) : (
        <div role="tabpanel" id={panelId(HARDWARE_TABS_PREFIX, tab)} aria-labelledby={tabId(HARDWARE_TABS_PREFIX, tab)} className="hw-panel">
          {tab === 'drivers' && <DriversTab snapshot={snapshot} links={links} status={status} actions={actions} />}
          {tab === 'kassas' && <KassasTab snapshot={snapshot} {...common} />}
          {tab === 'terminals' && <TerminalsTab snapshot={snapshot} {...common} />}
        </div>
      )}
    </section>
  )
}
